// Stale-deploy self-healing. `index.html` is served no-cache, but browsers
// (and aggressive proxies) can still hand back a previously-cached HTML
// document — which boots an old SPA that references chunk hashes the CDN has
// already deleted, or runs now-dead code paths. lazyWithRetry/ErrorBoundary
// recover only once a stale chunk actually 404s on navigation; this catches
// the case where no navigation happens (user idles on one screen) by polling
// a tiny `/version.json` and reloading onto the fresh build.

const VERSION_RELOAD_KEY = "wl_version_reload";
const POLL_INTERVAL_MS = 5 * 60 * 1000;

// Compiled-in build id of the bundle currently running in this tab.
const CURRENT_BUILD_ID = import.meta.env.VITE_BUILD_ID;

/**
 * Routes where a reload costs the user work they cannot get back.
 *
 * `/e/:token` is the customer approving an estimate: they type their name and
 * draw a signature, both of which live only in component state. The reloader
 * fires on `focus` and `visibilitychange` — precisely when someone tabs away
 * to check a figure and comes back — so a deploy shipping mid-signature wipes
 * it with no explanation and no draft. Observed happening on stage.
 *
 * `/q/:slug` is the intake form: a description the customer typed and photos
 * they picked, same reasoning.
 *
 * Skipping the reload here is the cheap side of the trade. These pages read
 * everything through an edge function and render one document; a stale bundle
 * costs a redundant fetch at worst. lazyWithRetry and the ErrorBoundary still
 * catch a genuinely dead chunk on navigation.
 */
function reloadWouldCostWork(): boolean {
  try {
    return /^\/(e|q)\//.test(window.location.pathname);
  } catch {
    return false;
  }
}

async function fetchDeployedBuildId(): Promise<string | null> {
  try {
    // cache:no-store so we always read the freshly-deployed manifest, never a
    // cached copy — otherwise the check itself could go stale.
    const res = await fetch("/version.json", { cache: "no-store" });
    if (!res.ok) return null;
    const data = (await res.json()) as { buildId?: unknown };
    return typeof data.buildId === "string" ? data.buildId : null;
  } catch {
    // Offline / blocked / non-JSON — treat as "no info", never disruptive.
    return null;
  }
}

/**
 * Watches for a newer deploy and force-reloads the tab once to pick it up.
 *
 * Guarded by a sessionStorage flag keyed on the *target* build id so a build
 * we can't actually load (e.g. CDN still propagating) doesn't reload-loop —
 * mirroring the chunk-reload guard in lazyWithRetry/ErrorBoundary.
 *
 * No-ops when the build isn't stamped (dev server) so HMR is untouched.
 */
export function initVersionCheck(): void {
  if (!CURRENT_BUILD_ID) return;

  let checking = false;
  const check = async (): Promise<void> => {
    if (checking || document.visibilityState === "hidden") return;
    checking = true;
    try {
      const deployed = await fetchDeployedBuildId();
      if (!deployed || deployed === CURRENT_BUILD_ID) {
        // Up to date — clear any stale guard so the next deploy re-arms.
        try {
          sessionStorage.removeItem(VERSION_RELOAD_KEY);
        } catch {
          // sessionStorage unavailable
        }
        return;
      }
      let alreadyReloaded = false;
      try {
        alreadyReloaded = sessionStorage.getItem(VERSION_RELOAD_KEY) === deployed;
        if (!alreadyReloaded) sessionStorage.setItem(VERSION_RELOAD_KEY, deployed);
      } catch {
        // sessionStorage unavailable — fall through and reload once.
      }
      // Never mid-signature. The next focus, or the poll after the customer
      // has finished and navigated away, picks the new build up.
      if (!alreadyReloaded && !reloadWouldCostWork()) window.location.reload();
    } finally {
      checking = false;
    }
  };

  // Re-check when the user returns to the tab (the moment they're most likely
  // to act on stale UI) and on a slow background interval as a backstop.
  window.addEventListener("focus", () => void check());
  document.addEventListener("visibilitychange", () => void check());
  window.setInterval(() => void check(), POLL_INTERVAL_MS);
}
